/**
 * Runs resolved actions for every surface (menus, toolbar, palette, shortcuts), so confirmations
 * (DESIGN.md T8) are handled in one place: an action with `confirm` opens the shared
 * `ConfirmDialog` and runs only after the user agrees. `AppShell` provides the runner.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../composites/confirm-dialog';
import { useUiStrings } from '../strings';
import { labelText, type ActionConfirm, type ResolvedAction } from './registry';

export type RunAction = <T>(entry: ResolvedAction<T>) => void;

const RunnerContext = createContext<RunAction | null>(null);

interface Pending {
  confirm: ActionConfirm;
  run: () => void | Promise<void>;
}

export function ActionRunnerProvider({ children }: { children: ReactNode }) {
  const { translate } = useUiStrings();
  const [pending, setPending] = useState<Pending>();

  const run = useCallback<RunAction>((entry) => {
    if (!entry.enabled) return;
    const confirm = entry.action.confirm?.(entry.context);
    const execute = () => entry.action.run(entry.context);
    if (confirm) setPending({ confirm, run: execute });
    else void execute();
  }, []);

  return (
    <RunnerContext.Provider value={run}>
      {children}
      {pending && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setPending(undefined)}
          title={labelText(translate, pending.confirm.title)}
          description={labelText(translate, pending.confirm.description)}
          confirmLabel={labelText(translate, pending.confirm.confirm)}
          onConfirm={pending.run}
        />
      )}
    </RunnerContext.Provider>
  );
}

/** Runs a resolved action (with its confirmation). Needs `ActionRunnerProvider` (part of `AppShell`). */
export function useRunAction(): RunAction {
  const run = useContext(RunnerContext);
  if (!run) throw new Error('useRunAction needs an ActionRunnerProvider (AppShell provides one).');
  return run;
}
